import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Divider, Skeleton } from "@heroui/react";
import cloneDeep from 'lodash.clonedeep';
import Papa from 'papaparse';

import {
  CellEditRequestEvent,
  ColDef,
  ColGroupDef,
  GetRowIdFunc,
  GetRowIdParams,
  GridApi,
  GridOptions,
  GridReadyEvent,
  RowDragEndEvent,
  RowDragMoveEvent,
  createGrid,
} from "@ag-grid-community/core";

import { store, useAppSelector } from '@/app/store';
import { RoleContext } from '@/app/api/common';
import { usePageSubscription } from '@/app/websocket/hooks';
//import { numericSort } from '@/app/api/common';
import { checkNoError, ChannelDescriptor, ChannelInfo, ChannelList, ChannelListManager, ChannelManager, copyChannelInfo, copyChannelDescriptor, copyChannelDescriptors, copyChannelList, copyChannelManager, getChannelByEpgId, getChannelListByEpgId, getNewChannelList, getUrl, getField, UrlInfo } from '@/features/channels/channelsSlice';
import { LanguageInfo } from '@/features/languages/languagesSlice';

import BreadCrumbs from '@/components/Common/BreadCrumbs';
import Button from '@/components/Common/Button';
import CheckBox from '@/components/Common/CheckBox';
import Dropdown, { FieldInfo } from '@/components/Common/Dropdown';
import Field from '@/components/Common/Field';
import FlexRow, { FlexRowProps } from '@/components/Common/FlexRow';
import Grid from '@/components/Common/Grid';
import Input from '@/components/Common/Input';
import MenuItem from '@/components/Common/MenuItem';
import { DeleteModal } from '@/components/Common/Modals';
import Tabs from '@/components/Common/Tabs';

import { CatHeader } from '@/pages/Projects/Project';
import Projects from '@/pages/Projects/Projects';
import ProjectRooms from '@/pages/Projects/ProjectRooms';
import { ChannelListsProps, getListPageUrl } from './ChannelLists';

export interface NumberTextProps {
  children?:			React.ReactNode;
}

const NumberText = (props: NumberTextProps) => {
  return (
  <span className="text-[#1B69AA]">
    { props.children }
  </span>
  );
};

const ChannelListComponent = (props: ChannelListsProps) => {
  const { t } = useTranslation();

  usePageSubscription(
    props.project
      ? [{ type: 'local_channel_list', scope_id: props.project.id }]
      : [{ type: 'global_channel_list' }]
  );

  const { id,epg_id, op, sub_tab, tab_no } = useParams();
  const [ params ] = useSearchParams();
  const epgId =	tab_no
  				?	tab_no
  				:	epg_id;
  
  const command =	sub_tab
	  				?	sub_tab
	  				:	op;
  
  const navigate = useNavigate();
  const globalLists = useAppSelector((state) => state.channelLists);
  const hotelChannelLists = useAppSelector((state) => state.hotelChannelLists);
  
  const channelLists =	props.project
  						?	hotelChannelLists
  						:	globalLists;  
  
  const channels = useAppSelector((state) => state.channels);
  const hotelChannels = useAppSelector((state) => state.hotelChannels);
  const projects = useAppSelector((state) => state.projects);
  const role = React.useContext(RoleContext);
  const logins = useAppSelector((state) => state.logins);

  const canEditLocalList = React.useMemo(() => {
    if (!props.project) return false;
    if (role.admin || role?.privilages?.includes("EditProjectSettings")) return true;

    const aclEnabled = props.project.limits?.acls
      ? props.project.limits.acls.some(u => u.type === "enable-channellist" && u.url === "1")
      : false;
    if (!aclEnabled) return false;
    const username = logins.login?.username;
    if (!username) return false;

    const localProjectId = logins.login?.local_project_id;
    if (localProjectId && localProjectId === props.project.id) return true;

    if (props.project.owner === username) return true;
    const execs = props.project.limits?.executives;
    return !!(execs?.partner?.includes(username) ||
              execs?.chain_manager?.includes(username) ||
              execs?.manager?.includes(username));
  }, [role, logins.login, props.project]);

  const requestMove = React.useRef((uid: number, by: number): any => 0);
  
  const [list, setList] = React.useState(null as ChannelList | null);
  const [changes, setChanges] = React.useState(false);
  const [triedSave, setTriedSave] = React.useState(false);
  const [channelsInvalid, SetChannelsInvalid] = React.useState(false);
  const [copyFrom, setCopyFrom] = React.useState("__none__");
  const [askDelete, setAskDelete] = React.useState(false);
  const [unavailableChannels, setUnavailableChannels] = React.useState<ChannelDescriptor[]>([]);
  
  // Hotel channels fetched with global=1, i.e. the hotel set merged with the
  // global channels a list may reference. Kept local instead of in the
  // hotelChannels slice, which must stay the hotel-only set for other pages.
  const [listChannels, setListChannels] = React.useState({channels: []} as ChannelManager);
  const [combinedChannels, setCombinedChannels] = React.useState({channels: []} as ChannelManager);
  const [combinedLists, setCombinedLists] = React.useState({lists: []} as ChannelListManager);
  
  const [copyOpts, setCopyOpts] = React.useState([] as FieldInfo[]);
  
  const [cols, setCols] = React.useState([] as any[]);
  const [rows, setRows] = React.useState(null as Array<ChannelDescriptor | ChannelInfo> | null);
  const [csvExport, setCsvExport] = React.useState(0);
  
  const [showSelectAll, setShowSelectAll] = React.useState(false);
  
  const handleTickAll = React.useRef((val: boolean, filtered: any[]) => { return; });

  // File input ref for CSV import
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [uploadedFileName, setUploadedFileName] = React.useState<string>('');

  const caption =(	epgId === "__new__" ||epgId === "Import")
  					?	t('channel_lists:new')
  					:	(	list
  							?	t("channel_lists:name_n_channels", {name: list.name, n: list.channels.length})
  							:	t('global:loading')
  						);
  
  //console.log("ChannelList/list=", list, "/rows=", rows, "/combinedChannels=", combinedChannels);
  
  React.useEffect(() => {
    if (	!channelLists.lists ||
        	channelLists.lists.length == 0
       )
      store.getChannelLists();
    
    if (	props.project &&
        	(	!hotelChannelLists.lists ||
        		hotelChannelLists.lists.length == 0
        	)
       )
        store.getChannelLists(props.project.id, false);
    
    /*if (	!channels.channels ||
        	channels.channels.length == 0
       ) {
      store.getChannels();
    }*/
    
    // Always fetch, instead of only when the hotelChannels slice happens to be
    // empty. That slice holds whatever the last caller asked for - the Channels
    // page fetches it without global=1 - so reusing it left every global channel
    // of the list unresolved in getChannelByEpgId(), and its Languages and
    // Replay cells rendered blank instead of the channel's real values.
    if (props.project) {
      store.fetchChannels(props.project.id, true).then((res) => {
        setListChannels({channels: res && res.channels ? res.channels : []});
      });
      store.getChannels();
    }
  }, []);
  
  React.useEffect(() => {
    const combined: ChannelManager = {
      channels: [],
    };
    const allowedIds: string[] = [];
    const addedIds: string[] = [];
    
    if (props.project) {
      for (const list of props.project.limits.channel_lists) {
        const chanList = getChannelListByEpgId(list.id, combinedLists);

        if (	!chanList ||
            	!chanList.channels
           )
          continue;

        for (const desc of chanList.channels)
          allowedIds.push(desc.epg_id);
      }

      const projectChannels =	listChannels.channels.length
      							?	listChannels
      							:	hotelChannels;

      for (const chan of projectChannels.channels) {
        combined.channels.push(copyChannelInfo(chan));
        addedIds.push(chan.epg_id);
      }

      if (projectChannels.channels.length === 0) {
        for (const cl of combinedLists.lists || []) {
          if (!cl.channels) continue;
          for (const ch of cl.channels) {
            if (addedIds.indexOf(ch.epg_id) !== -1) continue;
            combined.channels.push(copyChannelInfo(ch as any));
            addedIds.push(ch.epg_id);
          }
        }
      }
    }
    
    for (const chan of channels.channels) {
      if (	!props.project ||
      		(	allowedIds.indexOf(chan.epg_id) > -1 &&
          		addedIds.indexOf(chan.epg_id) == -1
          	)
         )
        combined.channels.push(copyChannelInfo(chan));
    }
    
    setCombinedChannels(combined);
  }, [channels, hotelChannels, listChannels, combinedLists]);
  
  React.useEffect(() => {
    const combined: ChannelListManager = {
      lists: [],
    };
    
    if (	!combined.lists ||
        	!globalLists.lists
       )
      return;
    
    const allowedIds: string[] = [];
    if (props.project) {
      for (const list of hotelChannelLists.lists) {
        combined.lists.push(copyChannelList(list));
      }
      
      for (const list of props.project.limits.channel_lists) {
        allowedIds.push(list.id);
      }
    }
    
    for (const list of globalLists.lists) {
      if (	!props.project ||
      		allowedIds.indexOf(list.epg_id) > -1
      	 )
        combined.lists.push(copyChannelList(list));
    }
    
    setCombinedLists(combined);
  }, [globalLists, hotelChannelLists, props.project]);
  
  React.useEffect(() => {
    //console.log("ChannelList/useEffect/epgId=" + epgId + "/channelLists=", channelLists, "/list=", list, "/project=", props.project);
    if (!epgId)
      return;
    
    const cOpts =
    	combinedLists &&
    	combinedLists.lists
    	?	combinedLists.lists.map((list: ChannelList) => ({
			  value:		list.epg_id,
			  desc:			t("channel_lists:name_n_channels", {name: list.name, n: list.channels.length}),
			}))
		:	[];
    
    cOpts.splice(0 , 0, {
      value:			"__none__",
      desc:				t("channel_lists:copy_list:__none__"),
    });
    
    setCopyOpts(cOpts);
    
    const cp =	params.get("clone");
    setCopyFrom(cp === null ? "__none__" : cp);
    
    if (	epgId === '__new__' &&
    		list &&
    		list.epg_id == ''
       )
      return;
    
    let nList =	(epgId === '__new__' || epgId === "Import")
				?	getNewChannelList()
				:	getChannelListByEpgId(epgId, combinedLists);
    
    if (nList)
      nList = copyChannelList(nList);
    
    setList(nList);
    setChanges(false);
    //console.log("ChannelList/epg_id=" + epg_id + "/nList=", nList);
  }, [epgId, params, combinedLists]);
  
  handleTickAll.current = React.useCallback((val: boolean, filtered: any[]) => {
    const nRows = cloneDeep(rows);
    if (!nRows)
      return;
    
    for (const filt of filtered) {
      for (let row of nRows) {
        const rowA = row as any;
        if (rowA.uid !== filt.uid)
          continue;
        
        row.ticked = val;
        break;
      }
    }
    
    setRows(nRows);
    setChanges(true);
  }, [rows]);
  
  React.useEffect(() => {
    switch(command) {
	case "view":
	case "delete":
	  setCols([
		{
			field:			"calculated_no",
			headerName:		t("channel_lists:list:grid:calculated_no"),
			flex:			0,
			minWidth:		130,
			maxWidth:		130,
		},
		{
			field:			"epg_id",
			headerName:		t("channel_lists:list:grid:epg_id"),
			flex:			0,
			minWidth:		150,
			maxWidth:		200,
		},
		{
			field:			"real_epg_id",
			headerName:		t("channel_lists:list:grid:real_epg_id"),
			hide:			true,
			flex:			0,
			minWidth:		150,
			maxWidth:		200,
		},
		{
			field:			"name",
			headerName:		t("channel_lists:list:grid:name"),
			flex:			1,
			minWidth:		200,
			//maxWidth:		300,
		},
		{
			field:			"languages",
			headerName:		t("channel_lists:list:grid:languages"),
			flex:			1,
			minWidth:		200,
			//maxWidth:		300,
		},
		{
			field:			"replay",
			headerName:		t("channel_lists:list:grid:replay"),
			flex:			0,
			minWidth:		100,
			maxWidth:		120,
		},
		{
			field:			"channel_no",
			headerName:		t("channel_lists:list:grid:channel_no"),
			flex:			0,
			minWidth:		150,
			maxWidth:		200,
		},
	  ]);
	  break;
	case "edit":
	  setCols([
		{
			field:			"ticked",
			headerName:		"",
		    headerComponent:		TickedHeader,
		    headerComponentParams:	{
		      showing:		props.setlocalChannel ? true : false,
		      showingFiltered:	showSelectAll,
		      tickAll:		(val: boolean, filtered: any[]) => handleTickAll.current && handleTickAll.current(val, filtered),
		    },
			flex:			0,
			filter:			false,
			minWidth:		50,
			maxWidth:		50,
			editable:		true,
		},
		{
			field:			"calculated_no",
			headerName:		t("channel_lists:list:grid:calculated_no"),
			flex:			0,
			minWidth:		130,
			maxWidth:		130,
			rowDrag:		(params: any) => params.data?.ticked === true,
		},
		{
			field:			"epg_id",
			headerName:		t("channel_lists:list:grid:epg_id"),
			flex:			0,
			minWidth:		150,
			maxWidth:		200,
		},
		{
			field:			"name",
			headerName:		t("channel_lists:list:grid:name"),
			flex:			1,
			minWidth:		200,
			//maxWidth:		300,
		},
		{
			field:			"real_epg_id",
			headerName:		t("channel_lists:list:grid:real_epg_id"),
			flex:			0,
			minWidth:		150,
			maxWidth:		200,
		},
		{
			field:			"languages",
			headerName:		t("channel_lists:list:grid:languages"),
			flex:			1,
			minWidth:		200,
			//maxWidth:		300,
		},
		{
			field:			"replay",
			headerName:		t("channel_lists:list:grid:replay"),
			flex:			0,
			minWidth:		100,
			maxWidth:		120,
		},
		{
			field:			"channel_no",
			headerName:		t("channel_lists:list:grid:channel_no"),
			editable:		true,
			flex:			0,
			minWidth:		150,
			maxWidth:		200,
		},
		{
		    field:			"move",
		    headerName:		t("channel_lists:list:grid:move"),
		    flex:			0,
		    filter:			false,
		    minWidth:		90,
		    maxWidth:		90,
		    cellRenderer:	MoveCell,
		},
	  ]);
	  break;
	}
  }, [command, rows, showSelectAll, t, props.project]);
  
  // epg_ids are matched exactly by getChannelByEpgId(). A list written by hand
  // or imported from CSV can differ from the catalog in case or padding only,
  // which resolved to null and blanked the row - keep a normalised index too.
  const normalisedChannels = React.useMemo(() => {
    const map = new Map<string, ChannelInfo>();
    
    // Priority order: the first manager holding an epg_id wins.
    for (const mgr of [combinedChannels, listChannels, hotelChannels, channels]) {
      for (const chan of (mgr && mgr.channels) || []) {
        const key = (chan.epg_id || "").trim().toLowerCase();
        
        if (key && !map.has(key))
          map.set(key, chan);
      }
    }
    
    return map;
  }, [combinedChannels, listChannels, hotelChannels, channels]);
  
  // The list payload can carry the catalog fields (urls, languages) on its own
  // entries; copyChannelList() strips them down to the descriptor, so index the
  // untouched slice once and keep it as a fallback for findChannel below.
  const rawListChannels = React.useMemo(() => {
    const map = new Map<string, any>();
    
    if (!epgId)
      return map;
    
    const raw =	getChannelListByEpgId(epgId, hotelChannelLists) ||
    			getChannelListByEpgId(epgId, globalLists);
    
    for (const desc of (raw && raw.channels) || [])
      map.set(desc.epg_id, desc);
    
    return map;
  }, [epgId, hotelChannelLists, globalLists]);
  
  // Resolve a list entry against every catalog we hold, not just
  // combinedChannels. That one is filtered down to the channels of the lists
  // assigned to the project, so an entry outside that set resolved to null and
  // its row kept the descriptor only - no languages, no urls - which is what
  // left the Languages and Replay cells blank for part of the list.
  const findChannel = React.useCallback((epg_id: string): ChannelInfo | null => {
    const known =	getChannelByEpgId(epg_id, combinedChannels) ||
    			getChannelByEpgId(epg_id, listChannels) ||
    			getChannelByEpgId(epg_id, hotelChannels) ||
    			getChannelByEpgId(epg_id, channels);
    
    if (known)
      return known;
    
    const loose = normalisedChannels.get((epg_id || "").trim().toLowerCase());
    
    if (loose)
      return loose;
    
    // Last resort: the list payload itself, which can carry the catalog fields
    // on its entries. copyChannelList() keeps only the descriptor fields, so
    // this reads them off the untouched slice.
    const raw = rawListChannels.get(epg_id);
    
    return	raw &&
    		(raw.urls || raw.languages)
    		?	raw as ChannelInfo
    		:	null;
  }, [combinedChannels, listChannels, hotelChannels, channels, normalisedChannels, rawListChannels]);
  
  React.useEffect(() => {
	let arr: Array<ChannelDescriptor | ChannelInfo> = [];
	
	switch(command) {
	case "view":
	case "delete":
	  if (	list &&
			list.channels
	     ) {
	    const channels =	list.channels
	    					?	list.channels
	    					:	[];
	    arr = channels.map((desc: ChannelDescriptor) => {
	      let chan: any = copyChannelDescriptor(desc);
	      chan.channel_no = 0;
	      chan.ticked = true;
	      const channel = findChannel(desc.epg_id);
	      // Set outside the guard: an entry with no matching channel has no
	      // replay url either, so it reads false instead of an empty cell.
	      chan.replay =	channel &&
	      				getUrl(channel.urls, "vod")
	      				?	"true"
	      				:	"false";
	      if (channel) {
	        chan.languages =	channel.languages
	        					?	channel.languages.join(",")
	        					:	"";
	        chan.real_epg_id = channel.real_epg_id;
	      }

	      return chan;
	    });
	  }
	  if (command == "delete")
	    setAskDelete(true);
	  break;
	case "edit":
	  if (	!list ||
	      	!list.channels ||
	  		!combinedChannels.channels
	     )
	    return;
	  
	  let chans = copyChannelManager(combinedChannels);
	  let added: string[] = [];
	  let changes = false;
	  
	  for(let lchan of list.channels) {
	    let nChan: any = copyChannelDescriptor(lchan);
	    nChan.channel_no = 0;
	    nChan.ticked = true;
	    
	    const channel = findChannel(nChan.epg_id);
	    nChan.replay =	channel &&
	    				getUrl(channel.urls, "vod")
	    				?	"true"
	    				:	"false";
	    if (channel) {
	      nChan.languages =	channel.languages
	       					?	channel.languages.join(",")
	       					:	"";
	    }

	    arr.push(nChan);
	    added.push(nChan.epg_id);
	  }
	    
	  for(let chan of chans.channels) {
	    if (added.indexOf(chan.epg_id) > -1)
	      continue;

	    const nChan = chan as any;
	    nChan.replay = getUrl(chan.urls, "vod") ? "true" : "false";
	    nChan.channel_no = 0;
	    nChan.ticked = nChan.ticked === true;
	    arr.push(nChan);
	    changes = true;
	  }
	  break;
	}

    assignUids(arr);
    calculateNumbering(arr, requestMove);
    console.log("ChannelListComponent/useEffect/arr=", arr);
    
    if (changes)
      setChanges(true);
	setRows(arr);
  }, [combinedChannels, findChannel, list, command, t]);
  
  const nameInvalid = React.useMemo(() => {
    return !list || list.name.trim() =='';
  }, [list]);
  
  const updateName = React.useCallback((val: string) => {
    if (!list)
      return;
    
    let newList = copyChannelList(list);
    newList.name = val;
    
    setList(newList);
    setChanges(true);
  }, [list]);
  
  const updatedHidden = React.useCallback((val: boolean) => {
    if (!list)
      return;
    
    let newList = copyChannelList(list);
    newList.hidden = val;
    
    setList(newList);
    setChanges(true);
  }, [list]);

  // Helper to find column value by flexible matching
  const getColumnValue = (row: any, ...possibleNames: string[]): string => {
    const keys = Object.keys(row);
    for (const name of possibleNames) {
      // Exact match
      if (row[name] !== undefined) return row[name];
      // Case-insensitive match
      const found = keys.find(k => k.toLowerCase().replace(/[\s._-]/g, '') === name.toLowerCase().replace(/[\s._-]/g, ''));
      if (found) return row[found];
    }
    return '';
  };

  // Helper to check if CSV has required column
  const hasRequiredColumn = (row: any, ...possibleNames: string[]): boolean => {
    const keys = Object.keys(row);
    for (const name of possibleNames) {
      if (row[name] !== undefined) return true;
      const found = keys.find(k => k.toLowerCase().replace(/[\s._-]/g, '') === name.toLowerCase().replace(/[\s._-]/g, ''));
      if (found) return true;
    }
    return false;
  };

  // Handle CSV file upload
  const handleFileUpload = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !list) return;

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        // Check if CSV has data
        if (!results.data || results.data.length === 0) {
          store.dispatchError(t('channel_lists:import:empty_file'));
          return;
        }

        // Check if required columns exist
        const firstRow = results.data[0] as any;
        const hasEpgId = hasRequiredColumn(firstRow, 'epg_id', 'epgid', 'EPG ID', 'EPG_ID');
        const hasName = hasRequiredColumn(firstRow, 'name', 'channel_name', 'Channel Name', 'Channel N');

        if (!hasEpgId || !hasName) {
          store.dispatchError(t('channel_lists:import:invalid_columns'));
          return;
        }

        const newChannels: ChannelDescriptor[] = results.data.map((row: any, index: number) => {
          const channelNoValue = getColumnValue(row, 'channel_no', 'channelno', 'Channel No', 'Channel No.');
          const parsedChannelNo = parseInt(channelNoValue);

          return {
            epg_id: getColumnValue(row, 'epg_id', 'epgid', 'EPG ID', 'EPG_ID'),
            old_epg_id: '',
            name: getColumnValue(row, 'name', 'channel_name', 'Channel Name', 'Channel N'),
            channel_no: !isNaN(parsedChannelNo) ? parsedChannelNo : index + 1,
            ticked: true,
          };
        });

        const newList = copyChannelList(list);
        newList.channels = newChannels;
        setList(newList);
        setChanges(true);
        setUploadedFileName(file.name);
        setUnavailableChannels([]);
      },
      error: (error) => {
        console.error('CSV parse error:', error);
        store.dispatchError(t('channel_lists:import:parse_error'));
      }
    });

    // Reset file input so same file can be uploaded again
    event.target.value = '';
  }, [list, t]);

  // Trigger file input click
  const handleUploadClick = React.useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  // Remove uploaded file
  const handleRemoveFile = React.useCallback(() => {
    if (!list) return;
    const newList = copyChannelList(list);
    newList.channels = [];
    setList(newList);
    setUploadedFileName('');
    setChanges(false);
    setUnavailableChannels([]);
  }, [list]);

  const updateDesc = React.useCallback((i: number, desc: ChannelDescriptor) => {
    if (!list)
      return;
    
    let newList = copyChannelList(list);
    newList.channels[i] = desc;
    
    let invalid = false;
    const chans: string[] = [];
    
    for (let i=0; i < newList.channels.length; i++) {
      let chan = newList.channels[i];
      
      chan.is_invalid = chans.indexOf(chan.epg_id) > -1 ||
                        chan.epg_id == "";
      newList.channels[i] = chan;
      
      if (!chan.is_invalid) {
        chans.push(chan.epg_id);
      } else {
        invalid = true;
      }
    }
    
    SetChannelsInvalid(invalid);
    
    console.log("ChannelList/updateDesc/i=" + i + "/invalid=" + invalid + "/desc=", desc, "/list=", list);
    setList(newList);
    setChanges(true);
  }, [list]);
  
  const deleteDesc = React.useCallback((i: number) => {
    if (!list)
      return;
    
    let newList = copyChannelList(list);
    newList.channels.splice(i, 1);
    
    setList(newList);
    setChanges(true);
  }, [list]);
  
  const addDesc = React.useCallback(() => {
    if (!list)
      return;
    
    let newList = copyChannelList(list);
    newList.channels.push({
      epg_id:				"",
      old_epg_id:			"",
      name:					"",
      channel_no:			0,
    });
    
    console.log("ChannelList/addDesc/newList=", newList);
    setList(newList);
    setChanges(true);
  }, [list]);
  
  const clearDropHint = React.useCallback(() => {
    document.querySelectorAll('.mw-drop-above, .mw-drop-below')
            .forEach((el) => el.classList.remove('mw-drop-above', 'mw-drop-below'));
  }, []);

  const onRowDragMove = React.useCallback((event: RowDragMoveEvent) => {
    clearDropHint();

    const over = event.overNode;
    if (	!over ||
        	!over.data ||
        	over === event.node ||
        	!over.data.ticked ||
        	!event.node.data ||
        	!event.node.data.ticked
       )
      return;

    const el = document.querySelector(`.ag-row[row-id="${over.data.uid}"]`);
    if (!el)
      return;

    const movingIndex = event.node.rowIndex === null ? 0 : event.node.rowIndex;
    const overIndex = over.rowIndex === null ? 0 : over.rowIndex;

    el.classList.add(overIndex > movingIndex ? 'mw-drop-below' : 'mw-drop-above');
  }, [clearDropHint]);

  const onRowDragEnd = React.useCallback((event: RowDragEndEvent) => {
      clearDropHint();

      var movingNode = event.node;
      var overNode = event.overNode;
      var rowNeedsToMove = movingNode !== overNode;
      
      if (rowNeedsToMove) {
        // the list of rows we have is data, not row nodes, so extract the data
        var movingData = movingNode.data;
        var overData = overNode!.data;
        
        if (	!movingData.ticked ||
            	!overData.ticked
           )
          return;
        
        if (	!list ||
            	!list.channels ||
            	!rows
           )
          return;
        
        var fromIndex = rows.indexOf(movingData);
        var toIndex = rows.indexOf(overData);
        
        let nRows = cloneDeep(rows);
        if (!nRows)
          return;
        
        if (fromIndex == toIndex)
          return;
        
        moveInArray(nRows, fromIndex, toIndex);

        // Row order is the source of truth; channel_no stays 0 so
        // calculateNumbering produces sequential calculated_no. Writing
        // calculated_no back into channel_no turns every row into an
        // anchor and makes subsequent drags jump (e.g. 1 -> 10).
        calculateNumbering(nRows, requestMove);
        setRows(nRows);
        setChanges(true);
        //gridRef.current!.api.clearFocusedCell();
      }
  }, [list, rows, clearDropHint]);

  const getRowId = React.useCallback((params: GetRowIdParams) => {
    //console.log("ChannelListComponent/params=", params);
    
    return	params.data.uid.toString();
  }, []);
  
  requestMove.current = React.useCallback((uid: number, by: number) => {
    console.log("ChannelListComponent/requestMove/uid=" + uid + "/by=" + by);
    
    if (	!rows ||
    		by == 0
       )
      return;
    
    for(let i=0; i < rows.length; i++) {
      const row = rows[i] as any;
      if (row.uid != uid)
        continue;
      
      const to = i + by;
      if (	to < 0 ||
      		to >= rows.length
      	 )
        break;
      
      let nRows = cloneDeep(rows);
      if (!nRows)
        return;
      
      moveInArray(nRows, i, to);

      // Row order is the source of truth; channel_no stays 0 so
      // calculateNumbering produces sequential calculated_no (see onRowDragEnd).
      calculateNumbering(nRows, requestMove);
      console.log("ChannelListComponent/requestMove/uid=" + uid + "/by=" + by + "/rows=", rows, "/nRows=", nRows);
      
      setRows(nRows);
      setChanges(true);
      break;
    }
  }, [rows]);
  
  const onCellEditRequest = React.useCallback((event: CellEditRequestEvent) => {
    //console.log("ChannelListComponent/onCellEditRequest, event=", event);
    
    const nRows = cloneDeep(rows);
    if (!nRows)
      return;
    
    let changes = false;
    
    for(let i = 0; i < nRows.length; i++) {
      const row = nRows[i];
      if ((row as any).uid !== event.data.uid)
        continue;
      
      switch((event.column as any).colId) {
      case "ticked":
        if (row.ticked === event.newValue)
          continue;
        
        row.ticked = event.newValue;
        changes = true;
        break;
      case "channel_no":
        let rowDesc = row as ChannelDescriptor;
        const targetPos = parseInt(event.newValue) || 0;
        if (targetPos === 0 || targetPos === rowDesc.channel_no)
          continue;

        // Move channel to the target position (1-based)
        const toIndex = Math.max(0, Math.min(targetPos - 1, nRows.length - 1));
        moveInArray(nRows, i, toIndex);
        rowDesc.channel_no = 0;
        changes = true;
        break;
      }

      console.log("onCellEditRequest/event=", event, "/row=", row);

      if (changes) {
        break;
      }
    }

    if (changes) {
      //if (event.column.colId == "channel_no")
      calculateNumbering(nRows, requestMove);

      console.log("ChannelListComponent/onCellEditRequest/nRows=", nRows);
      setRows(nRows);
      setChanges(true);
    }
  }, [rows]);
  
  const processDelete = React.useCallback(async (evt: Object, answer: string) => {
    setAskDelete(false);
    
    if (answer !== "yes") {
      if (	list &&
      		command == "delete"
      	 )
        navigate(getListPageUrl(list, props.project, "view"));
        //navigate(`/channel_lists/${list.epg_id}/view`);
      return;
    }
    
    if (	!list ||
        	list.is_new
       )
      return;
    
    if (nameInvalid || channelsInvalid) {
      setTriedSave(true);
      return;
    }
    
    let newList = copyChannelList(list);
    if (!newList)
      return;
      
    newList.to_delete = true;
    
    const mess = await store.setChannelList(newList, props.project ? props.project.id : "");
    console.log("ChannelList/processDelete/mess=", mess);

    if(mess?.errors[0].code === 423){
      handleRoomOrProject(id)
    }
    if (!checkNoError(mess)) {
      return;
    }
    
    if (	mess &&
            mess.errors &&
            mess.errors.length
       ) {
      navigate(getListPageUrl(null, props.project, ""));
      //navigate(`/channel_lists`);
      return;
    }
    
    setTriedSave(false);  
  }, [list, command, props.project]);
  
  const filterApplied = (evt: any) => {
    if (	!evt ||
    		!evt.api
       )
      return;
    
    console.log("ChannelList/filterApplied/evt=", evt);
    
    if (	!evt.api.isQuickFilterPresent() &&
    		!evt.api.isColumnFilterPresent()
       ) {
      setShowSelectAll(false);
      return;
    }
    
    setShowSelectAll(true);
  };
  
  const handleSave = async () => {
    if (!list)
      return;
    
    if (nameInvalid || channelsInvalid) {
      setTriedSave(true);
      return;
    }
    
    const wasNew = list.is_new;
    let newList = copyChannelList(list);
    if (!newList)
      return;
    
    if (	wasNew &&
    		copyFrom != '' &&
    		copyFrom != "__none__" 
       ) {
      const copyList = getChannelListByEpgId(copyFrom, combinedLists);
      console.log("handleSave/wasNew=" + wasNew + "/copyFrom=" + copyFrom + "/copyList=", copyList, "/newList=", newList);
      newList.channels =	copyList
      						?	copyChannelDescriptors(copyList.channels)
      						:	[];
    }
    
    if (	command &&
    		command === "edit"
       ) {
      const channels: ChannelDescriptor[] = [];
      
      if (!rows)
        return;
      
      // Every row now carries channel_no, whether it came from the list or
      // from the available channels, so the shape no longer has to be probed —
      // ticked alone decides what ends up in the list, in row order.
      for(const row of rows) {
        if (!row.ticked)
          continue;

        channels.push(copyChannelDescriptor(row as ChannelDescriptor));
      }

      // Reset channel_no to sequential order (reordering already happened in the grid)
      for (let i = 0; i < channels.length; i++) {
        channels[i].channel_no = i + 1;
      }

      newList.channels = channels;
    }
    
    const isImport = epgId === "Import";
    const importAnyway = isImport && unavailableChannels.length > 0;
    const mess = isImport
      ? await store.verifyAndSaveChannelList(newList, props.project ? props.project.id : "", importAnyway)
      : await store.setChannelList(newList, props.project ? props.project.id : "");
    console.log("ChannelList/handleSave/command=" + command + "/isImport=" + isImport + "/importAnyway=" + importAnyway + "/mess=", mess);

    if (isImport && mess && mess.errors && mess.errors.length && mess.errors[0].code === 422) {
      store.dispatchError("");
      setUnavailableChannels(mess.errors[0].result || []);
      return;
    }

    if (!checkNoError(mess))
      return;
    
    if (	mess &&
            mess.errors &&
            mess.errors.length
       ) {
      let url = getListPageUrl(null, props.project, "");
      
      if (command != "")
        url += `/${mess.errors[0].result.epg_id}/view`;
      navigate(url);
      
      //navigate(`/channel_lists/${mess.errors[0].result.epg_id}/view`);
      return;
    }
    
    setTriedSave(false);
    setChanges(false);
  };
  
  const cloneList = () => {
    if (!list)
      return;
    
    setCopyFrom(list.epg_id);
    
    const params = new URLSearchParams({
      clone:		list.epg_id,
    });
    
    let url = getListPageUrl(null, props.project, "__new__");
    url += '?' + params.toString();
    navigate(url);
    //navigate('/channel_lists/__new__?' + params.toString());
  };

  const handleRoomOrProject = async (id: any) => {
    if (id) {
      const rooms = await store.getChannelListByRoom(id as string, tab_no as string);
      navigate(getListPageUrl(list, props.project, "searchroom"));
    } else {
      const projects = await store.getChannelListByProject(epg_id as string);
      navigate(getListPageUrl(list, props.project, "searchproject"));
    }
  }
  const crumbs = [];
  if (!props.project)
    crumbs.push([
    	`/channel_lists`,
     	t('channel_lists:caption'),
    ]);
    
    const listUrl = command === 'searchproject'
    ? getListPageUrl(list, props.project, "view")
    : ( command === 'searchroom'
        ? getListPageUrl(list, props.project, "view")
        : ''
      );
  crumbs.push([listUrl, caption]);
 if(command=='searchroom')
    crumbs.push([
    	``,
     	t('channel_lists:options:search_rooms'),
    ])
  if(command=='searchproject')
    crumbs.push([
    	``,
     	t('channel_lists:options:search_projects'),
    ])
  
  const isNew = list && list.is_new;
  const loading = !list;
  
  const heading =	(): string => {
  	if (isNew)
  	  return 'channel_lists:add_new';
  	
  	switch(command) {
	case "view":
	case "delete":
	  return 'channel_lists:view_list'
	  break;
	case "edit":
	default:
	  return 'channel_lists:options:edit_list'
	  break;
	};
  }
  
  switch(command) {
  case "view":
  case "delete":
  case "edit":
  case "searchproject":
  case "searchroom":
    const grid = (
	  <Skeleton isLoaded={!loading && (rows !== null) ? true : false}
				className="w-full">
		<Grid rowData={rows}
		      columnDefs={cols}
		      filter={command && command == "edit"}
              globalFilter={true}
              onFilterChanged={filterApplied}
		      showPages={false}
		      getRowId={getRowId}
		      onCellEditRequest={onCellEditRequest}
		      onRowDragMove={onRowDragMove}
		      onRowDragLeave={clearDropHint}
		      onRowDragEnd={onRowDragEnd}
		      animateRows={true}
		      readOnlyEdit={true}
		      csvFile={list ? list.name +  '-channels.csv' : ''}
          	  csvExport={csvExport}
		      stopEditingWhenCellsLoseFocus={true}
		      //suppressCellFocus={true}
		      singleClickEdit={true}
		      pageSize={50000}
		      maxHeight={props.project ? "calc(100vh - 400px)" : "calc(100vh - 280px)"} />
	  </Skeleton>
	  );
    
    return (
    <React.Fragment>
      <FlexRow direction="column"
               className={[
                 props.project ? '' : 'px-[32px] pt-[32px] sticky top-[64px] z-50',
               ]}>
	    <React.Fragment>
	      <FlexRow direction="column"
	               align="start"
	               className="pb-[8px] bg-white">
	        <BreadCrumbs pages={crumbs} />
	      </FlexRow>
	      <Divider className="my-[4px]" />
	    </React.Fragment>
	    <FlexRow className="pt-[4px] pb-[16px] gap-[16px]">
	    {command && (role.admin || (!props.project && role?.privilages?.includes("ChangeGlobalChannelLists")) || (props.project && canEditLocalList)) && (command == 'view' || command == 'delete') &&
	      <Button caption={t('channel_lists:options:edit_list')}
                  className="uppercase"
                  icon="edit_note"
                  onClick={() => { store.clearChannelListProjects(); list && navigate(getListPageUrl(list, props.project, "edit")); }} />
	    }
	    {command && command == 'edit' && (role.admin || (!props.project && role?.privilages?.includes("ChangeGlobalChannelLists")) || (props.project && canEditLocalList)) &&
	      <Button caption={t('channel_lists:options:save_changes')}
                  className="uppercase"
                  isDisabled={!changes || (triedSave && (nameInvalid || channelsInvalid))}
                  onClick={() => handleSave()} />
	    }
      {
        role.admin && command !== 'searchproject' && command !== 'searchroom' &&
	      <Button caption={t('channel_lists:options:clone_list')}
                  className="uppercase"
                  icon="content_copy"
                  isDisabled={triedSave && (nameInvalid || channelsInvalid)}
                  onClick={() => cloneList()}
                  layout="white" />
      }
      {!(id ? command === 'searchroom' : command === 'searchproject') &&
        <Button caption={t(id ? 'channel_lists:options:search_rooms' : 'channel_lists:options:search_projects')}
                  className="uppercase"
                  icon="search"
                  isDisabled={triedSave && (nameInvalid || channelsInvalid)}
                  onClick={() => handleRoomOrProject(id)}
                  layout="white" />
      }
      {role?.admin && command !== 'searchproject' && command !== 'searchroom' &&
	      <Button caption={t('channel_lists:options:delete_list:caption')}
                  className="uppercase"
                  icon="delete"
                  onClick={() => setAskDelete(true)}
                  layout="warning" />
      }
          <FlexRow />
        {	(command && command === "view" && rows && rows.length) ||
	        (command === "searchproject" && ((projects.channelListProjects && projects.channelListProjects.length > 0) || (list && list.channels && list.channels.length > 0))) ||
	        (command === "searchroom" && ((projects.channelListRooms && projects.channelListRooms.length > 0) || (list && list.channels && list.channels.length > 0)))
        	?	<Button caption={t('channel_lists:options:export')}
				        className="uppercase"
				        icon="share"
				        onClick={() => setCsvExport(csvExport + 1)} />
			: null
	    }
	    </FlexRow>
	    { command !== 'searchproject' && command !== 'searchroom' && grid }
	    { command === 'searchproject' && <Projects hideContainer={true} csvExport={csvExport} csvFile={list ? list.name + '-projects.csv' : 'projects.csv'} /> }
	    { command === 'searchroom' && <ProjectRooms hideContainer={true} csvExport={csvExport} csvFile={list ? list.name + '-rooms.csv' : 'rooms.csv'} /> }
	  </FlexRow>
	{askDelete &&
	  <DeleteModal title={t('channel_lists:options:delete_list:title')}
	               message={t('channel_lists:options:delete_list:message')}
	               onResult={processDelete}>
        <>
        </>
	  </DeleteModal>
	}
	</React.Fragment>
    );
    break;
  }
  
  return (
  <FlexRow direction="column">
	<FlexRow direction="column"
	         align="start"
	         className="pt-[32px] px-[32px] bg-white sticky top-[64px] z-50">
	  <BreadCrumbs fontSize={!op ? 'm14' : 'r32'}
	               pages={crumbs} />
	</FlexRow>
    <FlexRow direction="column"
             align="start"
             className="px-[32px]">
      <FlexRow direction="column"
		       align="start"
		       className="py-0 gap-[16px] max-w-[620px]">
		<CatHeader caption={heading()} />
		<Input label={t('channel_lists:name:caption') + '*'}
		       placeholder={t('channel_lists:name:placeholder')}
		       isLoaded={!loading}
		       isInvalid={triedSave && nameInvalid}
		       errorMessage={t('channel_lists:name:invalid')}
		       value={list ? list.name : ''}
		       onValueChange={updateName} />
	  {list && list.is_new && epgId !== "Import" &&
	    <Dropdown label={t('channel_lists:copy_list:caption')}
		          value={copyFrom}
		          options={copyOpts}
		          setValue={setCopyFrom}>
	      <>
		  </>
		</Dropdown>
	  }
    { (epgId === "Import" && (role.admin || (props?.setlocalChannel && (role?.privilages?.includes("EditProjectSettings") || canEditLocalList)))) &&
      <Field label={t('channel_lists:options:upload')}
             labelWide={true}>
        <input
          type="file"
          accept=".csv"
          ref={fileInputRef}
          onChange={handleFileUpload}
          style={{ display: 'none' }}
        />
        {!uploadedFileName ? (
          <Button className="uppercase"
                  caption={t('channel_lists:options:upload')}
                  icon="file_upload"
                  onClick={handleUploadClick} />
        ) : (
          <FlexRow className="gap-[8px] items-center">
            <span className="material-icons !text-[20px] text-green-600">check_circle</span>
            <span className="text-sm truncate max-w-[200px]">{uploadedFileName}</span>
            <span
              className="material-icons !text-[20px] text-red-500 cursor-pointer hover:text-red-700"
              onClick={handleRemoveFile}>
              close
            </span>
          </FlexRow>
        )}
      </Field>
    }
    { (epgId === "Import" && unavailableChannels.length > 0) &&
      <FlexRow direction="column" align="start" className="gap-[4px] w-full">
        <span className="text-sm text-red-600 font-medium">
          {t('channel_lists:import:unavailable_message')}
        </span>
        <ul className="list-disc pl-[20px] w-full max-h-[300px] overflow-y-auto">
          {unavailableChannels.map((ch, i) => (
            <li key={i} className="text-sm text-red-600">
              {ch.name}
            </li>
          ))}
        </ul>
      </FlexRow>
    }
	  {epgId !== "Import" &&
		<Field label={t('channel_lists:hidden:caption')}
		       labelWide={true}>
		  <CheckBox toggled={list ? list.hidden : false}
		            setToggled={updatedHidden}
		            isLoaded={!loading} />
		</Field>
	  }
	  </FlexRow>
    </FlexRow>
    <FlexRow className="absolute bottom-0 p-[32px] bg-white z-10 justify-end gap-[16px]">
      <Button className="uppercase"
              caption={t('channel_lists:cancel')}
              onClick={() => navigate(getListPageUrl(null, props.project, ""))}
              layout="white" />
      {(role.admin || (!props.project && role?.privilages?.includes("ChangeGlobalChannelLists")) || (props.project && canEditLocalList)) &&
      <Button className="uppercase"
              caption={(epgId === "Import" && unavailableChannels.length > 0) ? t('channel_lists:import:import_anyway') : t('channel_lists:save')}
              isDisabled={!changes || (triedSave && (nameInvalid || channelsInvalid))}
              onClick={() => handleSave()} />
      }
    </FlexRow>
  </FlexRow>
  );
};

const MoveCell = (props: any) => {
  if (!props.data.ticked)
    return null;

  return (
  <FlexRow className="gap-[10px] h-full">
    <div className="material-icons !text-black text-[16px] cursor-pointer"
         onClick={() => props.data.requestMove.current(props.data.uid, -1)}>
      north
    </div>
    <div className="material-icons !text-black text-[16px] cursor-pointer"
         onClick={() => props.data.requestMove.current(props.data.uid, +1)}>
      south
    </div>
  </FlexRow>
  );
};

function moveInArray(arr: any[], fromIndex: number, toIndex: number) {
  var element = arr[fromIndex];
  arr.splice(fromIndex, 1);
  arr.splice(toIndex, 0, element);
}

function assignUids(channels: Array<ChannelDescriptor | ChannelInfo>) {
    if (!channels)
      return;

    let uid = 0;

    for(let chan of channels)
      (chan as any).uid = uid++;
}

function calculateNumbering(channels: Array<ChannelDescriptor | ChannelInfo>, requestMove: any) {
    if (	!channels ||
    		!channels.length
       )
      return;

    let last_no = 1;

    for(let chan of channels) {
	  let achan = (chan as any);
	  achan.requestMove = requestMove;

      if (!chan.ticked) {
        // Not part of the list, so it has no position to show.
        delete achan.calculated_no;
        continue;
      }

      let desc = chan as ChannelDescriptor;

      if (	desc.channel_no != 0 &&
      		desc.channel_no >= last_no
      	 ) {
        last_no = desc.channel_no;
      }
      
      desc.calculated_no = last_no;
      last_no++;
    }
}

const TickedHeader = (props: any) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const allRows: any[] = [];
  props.api.forEachNode((row: any) => allRows.push(row.data));
  const filteredRows: any[] = [];
  props.api.forEachNodeAfterFilter((row: any) => filteredRows.push(row.data));

  const isFiltered = props.column.colDef.headerComponentParams?.showingFiltered;
  const rowsToCheck = isFiltered ? filteredRows : allRows;
  if (	!props.column.colDef.headerComponentParams ||
     	!props.column.colDef.headerComponentParams.showing ||
     	rowsToCheck === undefined
     )
    return null;

  let allTicked =	rowsToCheck &&
  					rowsToCheck.length > 0
  					?	true
  					:	false;

  for (const row of rowsToCheck) {
    if (row.ticked)
      continue;

    allTicked = false;
    break;
  }

  console.log("TickedHeader/allTicked=" + allTicked + "/isFiltered=" + isFiltered + "/props=", props, "/rowsToCheck=", rowsToCheck);

  const handleTicked = (evt: any) => {
    //console.log("TickedHeader/handleTicked/evt=", evt);
    if (	props.column.colDef.headerComponentParams &&
    		props.column.colDef.headerComponentParams.tickAll
       )
      props.column.colDef.headerComponentParams.tickAll(evt.target.checked, rowsToCheck);
  };

  //  ag-checkbox-input-wrapper

  return (
  <FlexRow justify="center"
           className="ag-cell-wrapper ag-checkbox-cell">
	<div className="ag-checkbox ag-input-field">
	  <div className={
	         'ag-wrapper ag-input-wrapper ag-checkbox-input-wrapper' +
	         (allTicked ? ' ag-checked' : '')
	       }>
	    <input className="ag-input-field-input ag-checkbox-input"
	           type="checkbox"
	           aria-live="polite"
	           checked={allTicked}
	           onChange={handleTicked} />
      </div>
    </div>
  </FlexRow>
  );
};

/*
const ChannelListViewer = () => {
  const { t } = useTranslation();
  
  const { epg_id, op } = useParams();
  const navigate = useNavigate();
  const channelLists = useAppSelector((state) => state.channelLists);
  
  
};*/

export default ChannelListComponent;


